import React, { Component } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  adoptPrompt,
  getPromptVersions,
  parseApiError,
  publishPrompt,
  rollbackPrompt,
  savePromptDraft,
  SESSION_EXPIRED
} from '../../services/moderationDashboardService';
import { diffPromptLines } from './PromptDiff';
import { fallbackTitle, titleOf } from './Agents';

// Éditeur d'UNE consigne de modération (B5_01, Phase 2) — monté par Agents.jsx dans la carte
// `Paper` de chaque agent de verdict (jamais dans celle de l'agent de contexte, DA21).
//
// ⚠ Ce que cet écran commande n'est pas un réglage local : c'est la consigne qui juge les
//   commentaires de tous les clients, immédiatement (un PATCH réussi est actif pour tout le parc
//   dans la seconde). Chaque garde-fou ci-dessous existe pour qu'aucune écriture ne parte sans
//   avoir été méritée — et pour qu'aucun échec ne perde jamais le texte saisi.
//
// Contrats consommés : CD6 (registre + réconciliation, via l'entrée jointe fournie par
// Agents.jsx), CD7 (adoption), CD8 (brouillon), CD9 (publication), CD10 (retour arrière),
// CD11 (historique), et la fonction pure `diffPromptLines` de B5_02 (D22).
//
// ⚠ Class component, pas de hooks — convention du repo (super-admin/CLAUDE.md:35).
// ⚠ Import circulaire ASSUMÉ avec ./Agents (Agents monte <PromptEditor/>, PromptEditor nomme les
//   agents avec les helpers d'Agents) — sûr parce que rien n'est appelé au chargement du module
//   (même pattern que PromptDiff.jsx).
//
// Décisions locales tranchées avec Mathias le 2026-08-29 (Phase 3 /dev) — DECISIONS.md :
//   L1 verrou invisible au chargement → refus tardif géré (403 write_locked, texte conservé)
//   L2 brouillon d'un autre admin      → écraser derrière une confirmation nommée (draft ET publish)
//   L3 panne du fournisseur            → rédiger + brouillon possibles, publication bloquée
//   L4 sortie avec texte non enregistré → confirmation bloquante + beforeunload
//   L5 pré-remplissage                 → le brouillon s'il existe (origine affichée), sinon le
//                                        texte en service, sinon (consigne d'essai) le registre

// ─────────────────────────────────────────────────────────────────────────────
// Constantes du contrat
// ─────────────────────────────────────────────────────────────────────────────

// Plafond SERVEUR (CD8/CD9 : 400 invalid_instructions au-delà) — affiché en vif dans l'éditeur,
// pour que la limite ne soit pas découverte au moment de publier, après avoir écrit.
export const MAX_INSTRUCTIONS_LENGTH = 32768;

// Borne de l'appel à diffPromptLines (LCS en O(n·m) SANS garde, COHERENCE.md ligne 43 : 360 Ko
// pour 276 × 327 lignes, ~740 Mo sur deux textes de 13 600 lignes). PromptDiff.jsx est hors
// write-set : la borne se pose ici, côté appelant. 4 M cellules ≈ 2 000 × 2 000 lignes, soit
// 40× les consignes réelles (~300 lignes) — un brouillon collé au-delà tombe sur le seul delta net.
export const MAX_DIFF_CELLS = 4000000;

// Libellé D44 de la consigne d'essai (celui de la console Mistral — pas un second libellé).
export const TEST_PROMPT_LABEL = 'NE PAS UTILISER — consigne d\'essai de l\'outil d\'administration';

const RECONCILIATION = {
  NEVER_ADOPTED: 'never_adopted',
  IN_SYNC: 'in_sync',
  DRIFTED: 'drifted',
  UNKNOWN: 'unknown'
};

const SOURCE_DRAFT = 'draft';
const SOURCE_LIVE = 'live';
const SOURCE_REGISTRY = 'registry';

// Texte de la boîte de confirmation (ticket §2, mot pour mot — pas de `window.confirm()` nu).
const PUBLISH_CONSEQUENCE =
  'Cette consigne remplacera immédiatement celle qui juge les commentaires sur tous les sites. ' +
  'Il n\'existe pas d\'annulation : seul un retour arrière, qui publie une nouvelle version.';

// ─────────────────────────────────────────────────────────────────────────────
// Fonctions PURES exportées — testées dans src/tests/promptEditor.test.js
// ─────────────────────────────────────────────────────────────────────────────

// Compte en POINTS DE CODE, pas en unités UTF-16 (même comptage qu'Agents.jsx:497 : les prompts
// GC contiennent 9 emoji hors-BMP, que String.length compte double).
export function codePointLength(text) {
  if (typeof text !== 'string' || text.length === 0) return 0;
  return [...text].length;
}

// Nombre de lignes tel que diffPromptLines le voit (découpage sur `\n` seul, texte vide → 0).
function lineCount(text) {
  if (typeof text !== 'string' || text.length === 0) return 0;
  return text.split('\n').length;
}

// diffPromptLines BORNÉE : rend null (et n'appelle jamais lcsOps) au-delà de `maxCells`.
export function diffWithinBound(textA, textB, maxCells) {
  const bound = typeof maxCells === 'number' ? maxCells : MAX_DIFF_CELLS;
  const cells = lineCount(textA) * lineCount(textB);
  if (cells > bound) return null;
  return diffPromptLines(textA || '', textB || '');
}

// Delta de caractères entre le texte de référence et le texte à publier, pour la boîte de
// confirmation. `added`/`removed` = points de code des lignes ajoutées/retirées du diff ligne à
// ligne (une ligne modifiée compte des deux côtés) ; `net` = différence de longueur totale.
// Au-delà de la borne : `bounded: true`, seuls `net` et les longueurs sont fournis.
export function charDelta(reference, next, maxCells) {
  const referenceLength = codePointLength(reference);
  const nextLength = codePointLength(next);
  const net = nextLength - referenceLength;
  const diff = diffWithinBound(reference || '', next || '', maxCells);
  if (!diff) {
    return { added: null, removed: null, net, referenceLength, nextLength, bounded: true, lines: null };
  }
  let added = 0;
  let removed = 0;
  diff.rows.forEach((row) => {
    if (row.kind !== 'change') return;
    if (row.left !== null) removed += codePointLength(row.left);
    if (row.right !== null) added += codePointLength(row.right);
  });
  return {
    added,
    removed,
    net,
    referenceLength,
    nextLength,
    bounded: false,
    lines: { added: diff.stats.added, removed: diff.stats.removed, hunks: diff.hunks }
  };
}

// L5 — texte chargé dans la zone de saisie à l'ouverture, et sa PROVENANCE (toujours affichée).
//   brouillon (CD6 draft.instructions) → sinon texte en service (CD3) → sinon version courante
//   du registre (CD11, seule source pour la consigne d'essai, jamais servie par CD3).
export function pickBaseText({ draft, liveText, registryText }) {
  if (draft && typeof draft.instructions === 'string') {
    return { text: draft.instructions, source: SOURCE_DRAFT };
  }
  if (typeof liveText === 'string') return { text: liveText, source: SOURCE_LIVE };
  if (typeof registryText === 'string') return { text: registryText, source: SOURCE_REGISTRY };
  return { text: null, source: null };
}

// Jointure CD3 × CD6 (ticket §1bis — trois pièges, tous silencieux) :
//   1. clé : CD3 `agent.id` ↔ CD6 `agent_id` ;
//   2. régime : CD6 renomme `regime` en `agent_mode` (C2) — les helpers d'Agents.jsx lisent
//      `regime` → remap AU POINT DE JOINTURE, jamais une entrée CD6 brute passée aux helpers ;
//   3. CD6 imbrique `live.{version_active, status, status_detail}` et `live` peut être null.
// Rend, dans l'ORDRE DU REGISTRE (l'ordre fait foi), une entrée par consigne. Les cartes sont
// rendues depuis le registre : l'écran reste rempli quand CD3 est en 503.
//
// `liveText` : texte en service (CD3), `null` = inconnu (CD3 absent, agent non lu, ou consigne
// d'essai). Un agent lu `ok` SANS texte donne `''` (connu, vide) — décision locale 2026-08-29 :
// il reste éditable et publiable (publier est le geste de réparation d'une consigne effacée).
export function joinRegistryWithLive(registry, cd3) {
  const prompts = registry && Array.isArray(registry.prompts) ? registry.prompts : [];
  const agents = cd3 && Array.isArray(cd3.agents) ? cd3.agents : [];
  const byId = new Map(agents.map((agent) => [agent.id, agent]));
  const hasTestPrompt = prompts.some((p) => p.is_test_prompt === true);

  return prompts.map((p) => {
    const cd3Agent = byId.get(p.agent_id) || null;
    const isTest = p.is_test_prompt === true;
    // Forme CD3 reconstituée pour les helpers (regime ← agent_mode, C2).
    const shape = cd3Agent || {
      id: p.agent_id,
      product: p.product,
      object: p.object,
      regime: p.agent_mode,
      console_name: p.console_name,
      status: p.live ? p.live.status : null
    };
    let title;
    if (cd3Agent) title = titleOf(cd3Agent, false);
    else if (p.console_name) title = p.console_name;
    else if (isTest) title = TEST_PROMPT_LABEL;
    else title = fallbackTitle(shape, false);

    const live = p.live || null;
    const liveStatus = live ? live.status : cd3Agent ? cd3Agent.status : null;
    const liveText =
      cd3Agent && cd3Agent.status === 'ok'
        ? typeof cd3Agent.instructions === 'string'
          ? cd3Agent.instructions
          : ''
        : null;

    return {
      agentId: p.agent_id,
      isTest,
      product: p.product,
      object: p.object,
      regime: p.agent_mode,
      title,
      cd3: cd3Agent,
      reconciliation: p.reconciliation,
      live,
      liveStatus,
      liveVersion: live && typeof live.version_active === 'number' ? live.version_active : null,
      liveDetail: live ? live.status_detail || null : cd3Agent ? cd3Agent.status_detail || null : null,
      missing: liveStatus === 'missing',
      unreadable: liveStatus === 'unreadable',
      published: p.published || null,
      draft: p.draft || null,
      versionsCount: typeof p.versions_count === 'number' ? p.versions_count : 0,
      liveText,
      liveTextKnown: typeof liveText === 'string',
      // D29/D37 : hors production (une consigne d'essai est servie), SEULE l'essai est publiable ;
      // en production (aucune consigne d'essai), les 6 le sont. Le serveur reste le garde
      // (400 publish_not_allowed_here) — l'écran ne fait que refléter.
      publishableHere: hasTestPrompt ? isTest : true
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers d'affichage (privés)
// ─────────────────────────────────────────────────────────────────────────────

// Texte de la ligne `published` d'un historique CD11 (la version courante du registre), ou null.
function registryTextFrom(versions) {
  if (!Array.isArray(versions)) return null;
  const current = versions.find((v) => v.status === 'published');
  return current && typeof current.instructions === 'string' ? current.instructions : null;
}

function formatDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR');
}

function formatNumber(n) {
  return typeof n === 'number' ? n.toLocaleString('fr-FR') : '—';
}

function authorName(author) {
  if (!author) return 'auteur inconnu';
  return author.username || author.id || 'auteur inconnu';
}

function signedNumber(n) {
  if (typeof n !== 'number') return '—';
  const abs = formatNumber(Math.abs(n));
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return '0';
}

const RECOVERY_LABELS = {
  record_only: 'Enregistrer ce changement à mon nom',
  adopt: 'Adopter l\'état réel comme référence'
};

// ─────────────────────────────────────────────────────────────────────────────
// Composant
// ─────────────────────────────────────────────────────────────────────────────

class PromptEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editing: false,
      dirty: false,
      // Texte chargé à l'ouverture et sa provenance (L5) — l'indicateur est toujours affiché.
      baseText: null,
      baseSource: null,
      // Mesures vivantes de la saisie (setState au plus une fois par frame, via rAF).
      measure: { count: 0, identical: true, empty: true, tooLong: false },
      // Historique CD11 — chargé au premier besoin (dépliage, diff de dérive, consigne d'essai).
      versions: null,
      versionsLoading: false,
      versionsError: null,
      historyOpen: false,
      // Diff de dérive (D22) : registre vs service.
      driftDiff: null,
      driftOpen: false,
      // Geste en cours : 'adopt' | 'draft' | 'publish' | 'rollback' | 'recover' | 'versions'.
      busy: null,
      // Dernier résultat (succès ou échec) — Alert fermable, avec ses éventuels boutons.
      notice: null,
      // Boîte de dialogue : { kind: 'publish'|'rollback'|'conflict', … }.
      confirm: null
    };
    this.inputRef = React.createRef();
    this.rafId = null;
    this.handleInput = this.handleInput.bind(this);
    this.startEditing = this.startEditing.bind(this);
    this.cancelEditing = this.cancelEditing.bind(this);
    this.resetToLive = this.resetToLive.bind(this);
    this.saveDraft = this.saveDraft.bind(this);
    this.openPublish = this.openPublish.bind(this);
    this.closeConfirm = this.closeConfirm.bind(this);
    this.confirmAction = this.confirmAction.bind(this);
    this.adopt = this.adopt.bind(this);
    this.toggleHistory = this.toggleHistory.bind(this);
    this.toggleDrift = this.toggleDrift.bind(this);
    this.dismissNotice = this.dismissNotice.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleNoteChange = this.handleNoteChange.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    this.setDirty(false);
  }

  // ── Texte courant ────────────────────────────────────────────────────────────

  currentText() {
    const node = this.inputRef.current;
    if (node && typeof node.value === 'string') return node.value;
    return this.state.baseText || '';
  }

  // Texte de RÉFÉRENCE du delta et du garde-fou « identique » : le texte en service (CD3) quand
  // il est connu ; pour la consigne d'essai (jamais servie par CD3), la version courante du
  // registre (in_sync ⇒ même texte). `null` = pas de référence → publication impossible.
  // ⚠ `versionsOverride` : juste après loadVersions(), `this.state.versions` peut ne pas être
  //   encore posé (batching React 18 dans les promesses) — on passe la valeur RÉSOLUE.
  referenceText(versionsOverride) {
    const { entry } = this.props;
    if (entry.liveTextKnown) return { text: entry.liveText, source: SOURCE_LIVE };
    const registry = this.registryText(versionsOverride);
    if (typeof registry === 'string') return { text: registry, source: SOURCE_REGISTRY };
    return { text: null, source: null };
  }

  registryText(versionsOverride) {
    return registryTextFrom(Array.isArray(versionsOverride) ? versionsOverride : this.state.versions);
  }

  measureText(text, versionsOverride) {
    const reference = this.referenceText(versionsOverride).text;
    const count = codePointLength(text);
    return {
      count,
      empty: text.trim().length === 0,
      // ⚠ Le plafond serveur se mesure en unités UTF-16 (`instructions.length`, CD8/CD9) — pas en
      //   points de code : un texte truffé d'emoji passerait ici et prendrait 400 là-bas.
      tooLong: text.length > MAX_INSTRUCTIONS_LENGTH,
      identical: typeof reference === 'string' && text === reference
    };
  }

  // ── Dirty / perte de saisie (L4) ─────────────────────────────────────────────

  setDirty(dirty) {
    if (this.state.dirty === dirty) {
      return;
    }
    if (this._isMounted) this.setState({ dirty });
    if (dirty) window.addEventListener('beforeunload', this.handleBeforeUnload);
    else window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.props.onDirtyChange) this.props.onDirtyChange(this.props.entry.agentId, dirty);
  }

  handleBeforeUnload(event) {
    // Avertissement NATIF du navigateur au rechargement / à la fermeture : le texte de la
    // réponse est ignoré par les navigateurs modernes, seul `returnValue` compte.
    event.preventDefault();
    event.returnValue = '';
    return '';
  }

  // Zéro setState par frappe : la mesure est posée au plus une fois par frame d'animation
  // (la zone de saisie est NON contrôlée — React ne touche jamais à `value`).
  handleInput() {
    if (this.rafId) return;
    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = null;
      if (!this._isMounted) return;
      const text = this.currentText();
      this.setState({ measure: this.measureText(text) });
      this.setDirty(text !== this.state.baseText);
    });
  }

  // D46 — `expected_updated_at` : le plus récent entre l'horodatage rendu par NOTRE dernier
  // enregistrement (`lastDraftUpdatedAt`, posé dans le .then de saveDraft) et celui de l'entrée
  // CD6 (rafraîchie seulement après le succès). Sans ça, un double-clic sur « Enregistrer le
  // brouillon » renvoie l'ancien horodatage et le serveur refuse — « brouillon d'un autre » qui
  // est le nôtre (revue de confirmation, 🔵 A).
  expectedDraftUpdatedAt(override) {
    if (override) return override;
    const { entry } = this.props;
    const candidates = [this.lastDraftUpdatedAt, entry.draft && entry.draft.updated_at]
      .filter(Boolean)
      .map((iso) => ({ iso, t: new Date(iso).getTime() }))
      .filter((c) => !Number.isNaN(c.t));
    if (!candidates.length) return null;
    return candidates.sort((a, b) => b.t - a.t)[0].iso;
  }

  // ── Historique CD11 (chargé une fois) ────────────────────────────────────────

  loadVersions() {
    const { entry } = this.props;
    if (Array.isArray(this.state.versions)) return Promise.resolve(this.state.versions);
    if (this.versionsPromise) return this.versionsPromise;
    const generation = this.versionsGeneration || 0;
    this.setState({ versionsLoading: true, versionsError: null });
    this.versionsPromise = getPromptVersions(entry.agentId)
      .then((result) => {
        this.versionsPromise = null;
        const versions = result && Array.isArray(result.versions) ? result.versions : [];
        // Une invalidation survenue pendant l'appel (publication, retour arrière) rend ce résultat
        // périmé : on ne l'écrit pas, on recharge.
        if ((this.versionsGeneration || 0) !== generation) return this.loadVersions();
        if (this._isMounted) this.setState({ versions, versionsLoading: false });
        return versions;
      })
      .catch((err) => {
        this.versionsPromise = null;
        const parsed = parseApiError(err);
        if (this._isMounted) this.setState({ versionsLoading: false, versionsError: parsed.message });
        throw err;
      });
    return this.versionsPromise;
  }

  // Après un geste qui change le registre, l'historique en mémoire est périmé.
  invalidateVersions() {
    this.versionsGeneration = (this.versionsGeneration || 0) + 1;
    this.setState({ versions: null, driftDiff: null }, () => {
      if (this.state.historyOpen) this.loadVersions().catch(() => {});
    });
  }

  // ── Édition ──────────────────────────────────────────────────────────────────

  editability() {
    const { entry } = this.props;
    if (entry.missing) {
      return { ok: false, reason: 'Cet agent n\'existe plus chez Mistral : rien à modifier.' };
    }
    if (entry.reconciliation === RECONCILIATION.NEVER_ADOPTED) {
      return {
        ok: false,
        reason: 'Aucune version de référence enregistrée : adopte d\'abord l\'état actuel (le bandeau ci-dessus).'
      };
    }
    return { ok: true, reason: null };
  }

  startEditing() {
    const { entry } = this.props;
    const open = (registryText, versions) => {
      const base = pickBaseText({ draft: entry.draft, liveText: entry.liveText, registryText });
      if (base.text === null) {
        this.setState({
          notice: {
            severity: 'warning',
            message:
              'Aucun texte de départ n\'est disponible pour cette consigne (ni brouillon, ni texte en service lisible, ni version au registre).'
          }
        });
        return;
      }
      this.setState({
        editing: true,
        baseText: base.text,
        baseSource: base.source,
        notice: null,
        measure: this.measureText(base.text, versions)
      });
      this.setDirty(false);
    };
    // L5/L3 : sans texte en service lisible (CD3 absent, agent non lu, consigne d'essai), le
    // registre (CD11) est à la fois la base de repli et le TEXTE DE RÉFÉRENCE du delta et du
    // garde-fou « identique » — on le charge d'abord, qu'un brouillon existe ou non.
    if (!entry.liveTextKnown) {
      this.setState({ busy: 'versions' });
      this.loadVersions()
        .then((versions) => {
          if (!this._isMounted) return;
          this.setState({ busy: null });
          open(registryTextFrom(versions), versions);
        })
        .catch(() => {
          if (!this._isMounted) return;
          this.setState({ busy: null });
        });
      return;
    }
    open(this.registryText(), this.state.versions);
  }

  cancelEditing() {
    if (this.state.dirty) {
      // eslint-disable-next-line no-alert
      const leave = window.confirm('Le texte modifié n\'est pas enregistré. Abandonner les modifications ?');
      if (!leave) return;
    }
    this.setDirty(false);
    this.setState({ editing: false, baseText: null, baseSource: null, notice: null });
  }

  // L5 — « repartir du texte en service » quand la zone porte le brouillon.
  resetToLive() {
    const { entry } = this.props;
    if (!entry.liveTextKnown) return;
    const node = this.inputRef.current;
    if (node) node.value = entry.liveText;
    this.setState({
      baseText: entry.liveText,
      baseSource: SOURCE_LIVE,
      measure: this.measureText(entry.liveText)
    });
    this.setDirty(false);
  }

  handleNoteChange(event) {
    const note = event.target.value;
    this.setState((prev) => (prev.confirm ? { confirm: { ...prev.confirm, note } } : null));
  }

  // ── Erreurs d'écriture (ticket §2 — chacune conserve le texte saisi) ─────────

  failureNotice(err, gesture) {
    const parsed = parseApiError(err);
    const body = parsed.body || {};
    const notice = { severity: 'error', message: parsed.message, code: parsed.error, gesture };

    switch (parsed.error) {
      case SESSION_EXPIRED:
        notice.title = 'Session expirée';
        notice.message =
          'La session a expiré : rien n\'a été envoyé. Recharge la page pour te reconnecter — le texte saisi est conservé dans cette zone tant que tu ne quittes pas l\'écran.';
        break;
      case 'write_locked':
        // L1 — refus tardif géré : le serveur rédige déjà la raison.
        notice.title = 'Publication verrouillée sur cet environnement';
        break;
      case 'publish_not_allowed_here':
        notice.title = 'Consigne non publiable depuis cet environnement';
        break;
      case 'agent_not_whitelisted':
        notice.title = 'Identifiant refusé';
        break;
      case 'prompt_drifted':
        notice.title = 'Consigne modifiée en dehors du dashboard';
        notice.refresh = true;
        break;
      case 'never_adopted':
        notice.title = 'Aucune version de référence';
        notice.refresh = true;
        break;
      case 'draft_conflict':
        // L2 — proposé à l'écrasement derrière une confirmation nommée (draft ET publish). Le refus
        // arrive AVANT tout PATCH : rien n'est parti chez Mistral. `refresh` recharge l'entrée pour
        // que `entry.draft` porte le brouillon de l'autre (et son `updated_at`).
        notice.title = 'Brouillon enregistré par quelqu\'un d\'autre';
        notice.conflict = body.current || null;
        notice.refresh = true;
        break;
      case 'identical_instructions':
      case 'invalid_instructions':
      case 'instructions_required':
      case 'target_is_current':
      case 'version_not_in_registry':
      case 'invalid_version':
      case 'note_required':
      case 'confirmation_required':
      case 'author_required':
        notice.title = 'Refusé par le serveur';
        break;
      case 'draft_required':
        // Rattrapage `record_only` sans brouillon à promouvoir : la seule issue est l'adoption de
        // l'état réel (comme `record_mismatch`).
        notice.title = 'Rattrapage impossible';
        notice.recovery = { available: false, next_step: 'adopt' };
        break;
      case 'record_mismatch':
        notice.title = 'Rattrapage impossible';
        notice.recovery = { available: false, next_step: 'adopt' };
        break;
      case 'agent_not_adoptable':
        notice.title = 'Adoption impossible';
        notice.severity = 'warning';
        break;
      case 'concurrent_adoption':
        notice.title = 'Adoption concurrente';
        notice.refresh = true;
        break;
      case 'mistral_not_configured':
      case 'mistral_unavailable':
        notice.title = 'État de la consigne non vérifiable';
        notice.severity = 'warning';
        notice.refresh = true;
        break;
      case 'mistral_write_failed':
        // D47 — l'état relu est INCHANGÉ : rien n'est enregistré, réessayer est légitime.
        notice.title = 'Mistral n\'a pas reçu la publication';
        notice.retryable = true;
        break;
      case 'publish_outcome_unknown':
        // D47 — l'écriture est partie, son issue est INCONNUE : ⛔ jamais « réessayer »
        // (une republication à l'aveugle publierait deux fois). Bouton piloté par recovery.
        notice.title = 'Issue de la publication inconnue — ne pas recommencer';
        notice.recovery = body.recovery || null;
        break;
      case 'registry_write_failed':
        // D47 / C11 — le PATCH est passé, seul le registre a échoué : suivre recovery.next_step.
        notice.title = 'Changement appliqué chez Mistral, mais non enregistré au registre';
        notice.recovery = body.recovery || null;
        break;
      default:
        notice.title = `Échec (${parsed.status ? `HTTP ${parsed.status}` : 'réseau'})`;
    }
    return notice;
  }

  fail(err, gesture) {
    if (!this._isMounted) return;
    const notice = this.failureNotice(err, gesture);
    this.setState({ busy: null, notice, confirm: null });
    if (notice.refresh && this.props.onChanged) this.props.onChanged();
  }

  // ── Gestes ───────────────────────────────────────────────────────────────────

  adopt() {
    const { entry, onChanged } = this.props;
    this.setState({ busy: 'adopt', notice: null });
    adoptPrompt(entry.agentId)
      .then((result) => {
        if (!this._isMounted) return;
        const outcomes = {
          adopted: 'État actuel adopté comme version de référence',
          updated: 'Numéro de version resynchronisé (texte inchangé)',
          unchanged: 'Déjà à jour : rien à adopter'
        };
        this.invalidateVersions();
        this.setState({
          busy: null,
          notice: {
            severity: 'success',
            message: `${outcomes[result.outcome] || `Adoption : ${result.outcome}`}${
              typeof result.mistral_version === 'number' ? ` — n° ${result.mistral_version} chez Mistral` : ''
            }.`
          }
        });
        if (onChanged) onChanged();
      })
      .catch((err) => this.fail(err, 'adopt'));
  }

  saveDraft(expectedUpdatedAt) {
    const { entry, onChanged } = this.props;
    const text = this.currentText();
    const measure = this.measureText(text);
    if (measure.empty || measure.tooLong) {
      this.setState({ measure });
      return;
    }
    const payload = { instructions: text };
    const expected = this.expectedDraftUpdatedAt(expectedUpdatedAt);
    if (expected) payload.expected_updated_at = expected;
    this.setState({ busy: 'draft', notice: null, confirm: null });
    savePromptDraft(entry.agentId, payload)
      .then((result) => {
        if (!this._isMounted) return;
        const draft = result && result.draft ? result.draft : null;
        this.lastDraftUpdatedAt = draft && draft.updated_at ? draft.updated_at : null;
        // Un historique déjà déplié porte l'ancienne ligne `draft` : à recharger.
        this.invalidateVersions();
        this.setState({
          busy: null,
          baseText: text,
          baseSource: SOURCE_DRAFT,
          notice: {
            severity: 'success',
            message: `Brouillon enregistré${draft && draft.updated_at ? ` le ${formatDate(draft.updated_at)}` : ''} — rien n'a été envoyé chez Mistral.`
          }
        });
        this.setDirty(false);
        if (onChanged) onChanged();
      })
      .catch((err) => this.fail(err, 'draft'));
  }

  publishability() {
    const { entry } = this.props;
    const { measure } = this.state;
    const reference = this.referenceText();
    if (entry.missing) return { ok: false, reason: 'Agent introuvable chez Mistral.' };
    if (!entry.publishableHere) {
      return {
        ok: false,
        reason: 'Hors production, seule la consigne d\'essai peut être publiée (D29).'
      };
    }
    if (entry.reconciliation === RECONCILIATION.DRIFTED) {
      return {
        ok: false,
        reason: 'Consigne modifiée en dehors du dashboard : adopte d\'abord l\'état réel (publier écraserait cette modification).'
      };
    }
    if (entry.reconciliation !== RECONCILIATION.IN_SYNC) {
      // L3 — unknown / never_adopted : on ne remplace pas une consigne qu'on ne sait pas lire.
      return { ok: false, reason: 'État de la consigne non vérifiable : la publication est bloquée, le brouillon reste possible.' };
    }
    if (typeof reference.text !== 'string') {
      return { ok: false, reason: 'Texte en service non lisible : la publication est bloquée.' };
    }
    if (measure.empty) return { ok: false, reason: 'Le texte est vide.' };
    if (measure.tooLong) {
      return { ok: false, reason: `Le texte dépasse ${formatNumber(MAX_INSTRUCTIONS_LENGTH)} caractères.` };
    }
    if (measure.identical) {
      return { ok: false, reason: 'Le texte est identique à celui en service : rien à publier.' };
    }
    return { ok: true, reason: null };
  }

  openPublish() {
    const text = this.currentText();
    const measure = this.measureText(text);
    this.setState({ measure });
    if (!this.publishability().ok) return;
    const reference = this.referenceText();
    // Un nouveau geste efface le résultat du précédent : le message affiché doit toujours être
    // celui de la dernière action.
    this.setState({
      notice: null,
      confirm: {
        kind: 'publish',
        text,
        note: '',
        referenceSource: reference.source,
        delta: charDelta(reference.text, text)
      }
    });
  }

  openRollback(version) {
    this.setState({
      notice: null,
      confirm: { kind: 'rollback', version, note: '' }
    });
  }

  closeConfirm() {
    if (this.state.busy) return;
    this.setState({ confirm: null });
  }

  confirmAction() {
    const { confirm } = this.state;
    if (!confirm) return;
    if (confirm.kind === 'publish') this.publish(confirm);
    else if (confirm.kind === 'rollback') this.rollback(confirm);
    else if (confirm.kind === 'conflict') this.overwrite(confirm);
  }

  publish(confirm, expectedUpdatedAt) {
    const { entry, onChanged } = this.props;
    const note = (confirm.note || '').trim();
    if (!note) return;
    // Conservé pour rejouer le même geste après un 409 draft_conflict (L2, openConflict).
    this.lastPublishConfirm = confirm;
    const payload = { instructions: confirm.text, note, confirm: true };
    const expected = this.expectedDraftUpdatedAt(expectedUpdatedAt);
    if (expected) payload.expected_updated_at = expected;
    this.setState({ busy: 'publish' });
    publishPrompt(entry.agentId, payload)
      .then((result) => {
        if (!this._isMounted) return;
        // Le brouillon vient d'être promu : plus d'horodatage à rejouer.
        this.lastDraftUpdatedAt = null;
        this.invalidateVersions();
        const verified = !result || result.verified !== false;
        this.setState({
          busy: null,
          confirm: null,
          editing: false,
          baseText: null,
          baseSource: null,
          notice: {
            severity: verified ? 'success' : 'warning',
            title: verified ? 'Consigne publiée' : 'Consigne publiée — application non confirmée',
            message: `Version interne ${formatNumber(result && result.internal_version)}, n° ${formatNumber(
              result && result.mistral_version
            )} chez Mistral${
              verified
                ? '.'
                : '. La relecture après publication n\'a pas confirmé que Mistral a appliqué ce texte : vérifie l\'état de la consigne (bandeau) avant toute autre modification.'
            }`
          }
        });
        this.setDirty(false);
        if (onChanged) onChanged();
      })
      .catch((err) => this.fail(err, 'publish'));
  }

  rollback(confirm, expectedUpdatedAt) {
    const { entry, onChanged } = this.props;
    const note = (confirm.note || '').trim();
    if (!note || !confirm.version) return;
    this.setState({ busy: 'rollback' });
    rollbackPrompt(entry.agentId, {
      internal_version: confirm.version.internal_version,
      note,
      confirm: true
    })
      .then((result) => {
        if (!this._isMounted) return;
        this.invalidateVersions();
        this.setState({
          busy: null,
          confirm: null,
          notice: {
            severity: 'success',
            title: 'Retour arrière effectué',
            message: `Nouvelle version interne ${formatNumber(result && result.internal_version)} (n° ${formatNumber(
              result && result.mistral_version
            )} chez Mistral), recopiant la version ${formatNumber(confirm.version.internal_version)}.`
          }
        });
        if (onChanged) onChanged();
      })
      .catch((err) => this.fail(err, 'rollback'));
  }

  // L2 — écrasement du brouillon d'un autre auteur, après confirmation nommée : le même geste
  // est rejoué avec `expected_updated_at` = l'horodatage que le serveur nous a montré.
  overwrite(confirm) {
    const expected = confirm.current && confirm.current.updated_at;
    if (!expected) {
      this.setState({ confirm: null, notice: { severity: 'warning', message: 'Horodatage du brouillon inconnu : recharge l\'onglet puis réessaie.' } });
      return;
    }
    if (confirm.gesture === 'draft') {
      this.saveDraft(expected);
    } else if (confirm.previous) {
      this.publish(confirm.previous, expected);
    } else {
      // Pas de publication mémorisée (rechargement entre-temps) : on repasse par la boîte de
      // confirmation — jamais un clic sans effet.
      this.setState({ confirm: null });
      this.openPublish();
    }
  }

  openConflict(notice) {
    // Le geste refusé pour conflit est rejoué depuis la boîte de confirmation « remplacer ».
    const previous = notice.gesture === 'publish' ? this.lastPublishConfirm : null;
    this.setState({
      confirm: { kind: 'conflict', gesture: notice.gesture, current: notice.conflict, previous },
      notice: null
    });
  }

  recover(recovery) {
    const { entry, onChanged } = this.props;
    if (!recovery) return;
    if (recovery.next_step === 'adopt') {
      this.adopt();
      return;
    }
    if (recovery.next_step !== 'record_only' || recovery.available === false) return;
    this.setState({ busy: 'recover', notice: null });
    publishPrompt(entry.agentId, { record_only: true })
      .then((result) => {
        if (!this._isMounted) return;
        this.lastDraftUpdatedAt = null;
        this.invalidateVersions();
        this.setState({
          busy: null,
          editing: false,
          baseText: null,
          baseSource: null,
          notice: {
            severity: 'success',
            title: 'Changement enregistré à ton nom',
            message: `Version interne ${formatNumber(result && result.internal_version)} enregistrée au registre (aucun nouvel envoi chez Mistral).`
          }
        });
        this.setDirty(false);
        if (onChanged) onChanged();
      })
      .catch((err) => this.fail(err, 'recover'));
  }

  toggleHistory() {
    const historyOpen = !this.state.historyOpen;
    this.setState({ historyOpen });
    if (historyOpen) this.loadVersions().catch(() => {});
  }

  // D22 — la différence entre le registre (ce qu'on croit publié) et le service (ce qui tourne),
  // montrée AVANT l'adoption.
  toggleDrift() {
    const { entry } = this.props;
    const driftOpen = !this.state.driftOpen;
    this.setState({ driftOpen });
    if (!driftOpen || this.state.driftDiff) return;
    this.loadVersions()
      .then((versions) => {
        if (!this._isMounted) return;
        const registry = registryTextFrom(versions);
        if (typeof registry !== 'string' || !entry.liveTextKnown) {
          this.setState({ driftDiff: { unavailable: true } });
          return;
        }
        const diff = diffWithinBound(registry, entry.liveText);
        this.setState({ driftDiff: diff ? { diff } : { bounded: true } });
      })
      .catch(() => {});
  }

  dismissNotice() {
    this.setState({ notice: null });
  }

  // ── Rendu ────────────────────────────────────────────────────────────────────

  renderReconciliation() {
    const { entry, liveError } = this.props;
    const { busy, driftOpen } = this.state;
    const testId = 'prompt-reconciliation';

    if (entry.reconciliation === RECONCILIATION.IN_SYNC) return null;

    if (entry.reconciliation === RECONCILIATION.NEVER_ADOPTED) {
      return (
        <Alert severity="info" sx={{ mb: 2 }} data-testid={testId} data-reconciliation="never_adopted">
          <AlertTitle>Aucune version de référence enregistrée</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            L'édition est désactivée tant que l'état actuel n'a pas été adopté comme point de départ :
            publier sans référence produirait un historique qui commence dans le vide.
          </Typography>
          <Button
            size="small"
            variant="contained"
            disabled={busy !== null || entry.missing}
            onClick={this.adopt}
            data-testid="prompt-adopt"
          >
            Adopter l'état actuel comme référence
          </Button>
        </Alert>
      );
    }

    if (entry.reconciliation === RECONCILIATION.DRIFTED) {
      const publishedVersion = entry.published ? entry.published.mistral_version : null;
      return (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid={testId} data-reconciliation="drifted">
          <AlertTitle>Cette consigne a été modifiée en dehors du dashboard</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Version de référence au registre : n° {formatNumber(publishedVersion)} chez Mistral — version
            réellement en service : n° {formatNumber(entry.liveVersion)}. L'édition reste possible, mais une
            publication <strong>écraserait</strong> la modification faite ailleurs : elle est refusée tant que
            l'état réel n'a pas été adopté.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={this.toggleDrift} data-testid="prompt-drift-toggle">
              {driftOpen ? 'Masquer la différence' : 'Voir la différence'}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={busy !== null}
              onClick={this.adopt}
              data-testid="prompt-adopt"
            >
              Adopter l'état actuel comme référence
            </Button>
          </Stack>
          <Collapse in={driftOpen} unmountOnExit>
            {this.renderDrift()}
          </Collapse>
        </Alert>
      );
    }

    // unknown — ⛔ jamais « à jour » : une absence d'information n'est pas une confirmation.
    const reason =
      liveError === 'mistral_not_configured'
        ? 'clé Mistral absente de cet environnement'
        : entry.missing
        ? 'agent introuvable chez Mistral'
        : entry.unreadable
        ? 'configuration illisible'
        : liveError
        ? 'Mistral injoignable'
        : 'état réel non lu';
    return (
      <Alert severity="info" sx={{ mb: 2 }} data-testid={testId} data-reconciliation="unknown">
        <AlertTitle>État de la consigne non vérifiable</AlertTitle>
        <Typography variant="body2">
          Impossible de comparer le registre à ce qui tourne réellement ({reason}). La publication et le
          retour arrière sont bloqués ; la rédaction et le brouillon restent possibles.
        </Typography>
      </Alert>
    );
  }

  renderDrift() {
    const { driftDiff, versionsLoading, versionsError } = this.state;
    if (versionsLoading) return <CircularProgress size={18} sx={{ mt: 1 }} />;
    if (versionsError) {
      return (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Historique illisible : {versionsError}
        </Typography>
      );
    }
    if (!driftDiff) return null;
    if (driftDiff.unavailable) {
      return (
        <Typography variant="body2" sx={{ mt: 1 }} data-testid="prompt-drift-unavailable">
          La différence ne peut pas être calculée : le texte en service ou celui du registre n'est pas lisible
          par cet écran (la consigne d'essai n'est jamais servie par la lecture des agents).
        </Typography>
      );
    }
    if (driftDiff.bounded) {
      return (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Textes trop longs pour un diff ligne à ligne : compare-les dans « Comparer les consignes ».
        </Typography>
      );
    }
    const { rows, stats, hunks } = driftDiff.diff;
    const changed = rows.filter((row) => row.kind === 'change');
    return (
      <Box sx={{ mt: 1 }} data-testid="prompt-drift-diff">
        <Typography variant="body2" sx={{ mb: 1 }}>
          {formatNumber(stats.common)} lignes communes · {formatNumber(stats.removed)} au registre seulement ·{' '}
          {formatNumber(stats.added)} en service seulement ({formatNumber(hunks)} bloc{hunks > 1 ? 's' : ''}).
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            maxHeight: 320,
            overflow: 'auto',
            bgcolor: 'action.hover',
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {changed.map((row, index) => (
            <React.Fragment key={index}>
              {row.left !== null && <Box component="span" sx={{ color: 'error.main', display: 'block' }}>{`− ${row.left}`}</Box>}
              {row.right !== null && <Box component="span" sx={{ color: 'success.main', display: 'block' }}>{`+ ${row.right}`}</Box>}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  }

  renderRegistryLine() {
    const { entry } = this.props;
    const { published, draft } = entry;
    return (
      <Box
        sx={{ mb: 1 }}
        data-testid="prompt-registry"
        data-internal-version={published ? published.internal_version : 'none'}
        data-mistral-version={published && typeof published.mistral_version === 'number' ? published.mistral_version : 'none'}
      >
        {published ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              Registre : version interne {formatNumber(published.internal_version)} (n°{' '}
              {formatNumber(published.mistral_version)} chez Mistral) — par {authorName(published.author)}
              {published.published_at ? `, le ${formatDate(published.published_at)}` : ''}
              {published.note ? ` — « ${published.note} »` : ''}
            </Typography>
            {published.verified === false && (
              <Chip size="small" color="warning" label="application non confirmée" data-testid="prompt-unverified" />
            )}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Registre : aucune version publiée.
          </Typography>
        )}
        {draft && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="prompt-draft-info">
            Brouillon enregistré{draft.updated_at ? ` le ${formatDate(draft.updated_at)}` : ''} par{' '}
            {authorName(draft.author)} ({formatNumber(codePointLength(draft.instructions))} caractères).
          </Typography>
        )}
      </Box>
    );
  }

  renderNotice() {
    const { notice, busy } = this.state;
    if (!notice) return null;
    const recovery = notice.recovery;
    const recoveryLabel = recovery && RECOVERY_LABELS[recovery.next_step];
    return (
      <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={this.dismissNotice} data-testid="prompt-notice">
        {notice.title && <AlertTitle>{notice.title}</AlertTitle>}
        <Typography variant="body2">{notice.message}</Typography>
        {notice.conflict && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Brouillon actuel : par {authorName(notice.conflict.author)}
            {notice.conflict.updated_at ? `, le ${formatDate(notice.conflict.updated_at)}` : ''}. Rien n'a été envoyé
            chez Mistral ; le texte saisi est conservé.
          </Typography>
        )}
        {recovery && typeof recovery.mistral_version === 'number' && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Le n° {formatNumber(recovery.mistral_version)} est actif chez Mistral mais n'est pas au registre.
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
          {notice.conflict && (
            <Button size="small" variant="outlined" disabled={busy !== null} onClick={() => this.openConflict(notice)} data-testid="prompt-conflict-replace">
              Remplacer le brouillon de {authorName(notice.conflict.author)}…
            </Button>
          )}
          {notice.retryable && notice.gesture === 'publish' && (
            <Button size="small" variant="outlined" disabled={busy !== null} onClick={this.openPublish} data-testid="prompt-retry">
              Réessayer
            </Button>
          )}
          {recovery && recoveryLabel && (recovery.available !== false || recovery.next_step === 'adopt') && (
            <Button size="small" variant="outlined" disabled={busy !== null} onClick={() => this.recover(recovery)} data-testid="prompt-recover">
              {recoveryLabel}
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  renderEditor() {
    const { entry } = this.props;
    const { baseText, baseSource, measure, busy, dirty } = this.state;
    const publishable = this.publishability();
    const reference = this.referenceText();
    const provenance =
      baseSource === SOURCE_DRAFT
        ? `Brouillon${entry.draft && entry.draft.updated_at ? ` du ${formatDate(entry.draft.updated_at)}` : ''}${
            entry.draft ? ` par ${authorName(entry.draft.author)}` : ''
          } — ce n'est PAS le texte en service.`
        : baseSource === SOURCE_LIVE
        ? 'Texte en service chez Mistral.'
        : `Version courante du registre (interne ${formatNumber(entry.published && entry.published.internal_version)}).`;

    return (
      <Box sx={{ mt: 1 }} data-testid="prompt-editor">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" data-testid="prompt-provenance">
            {provenance}
          </Typography>
          {baseSource === SOURCE_DRAFT && entry.liveTextKnown && (
            <Button size="small" onClick={this.resetToLive} disabled={busy !== null} data-testid="prompt-reset-live">
              Repartir du texte en service
            </Button>
          )}
        </Stack>
        {entry.liveTextKnown && entry.liveText === '' && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Aucune consigne en service chez Mistral : ce que tu publieras remplacera un texte vide.
          </Alert>
        )}
        {/* NON CONTRÔLÉ : `rows` fixé → <textarea> natif sans TextareaAutosize, `defaultValue` +
            `inputRef` — React ne re-rend jamais 13 600 caractères à chaque frappe. */}
        <TextField
          fullWidth
          multiline
          rows={24}
          defaultValue={baseText || ''}
          inputRef={this.inputRef}
          disabled={busy !== null}
          inputProps={{
            'data-testid': 'prompt-textarea',
            onInput: this.handleInput,
            spellCheck: false,
            style: { fontFamily: 'monospace', fontSize: '0.8rem' }
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mt: 1 }}>
          <Typography variant="caption" color={measure.tooLong ? 'error' : 'text.secondary'} data-testid="prompt-counter">
            {formatNumber(measure.count)} / {formatNumber(MAX_INSTRUCTIONS_LENGTH)} caractères
            {typeof reference.text === 'string'
              ? ` · ${signedNumber(measure.count - codePointLength(reference.text))} par rapport ${
                  reference.source === SOURCE_LIVE ? 'au texte en service' : 'à la version du registre'
                }`
              : ''}
            {dirty ? ' · modifié, non enregistré' : ''}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" alignItems="center">
          <Button
            size="small"
            variant="outlined"
            disabled={busy !== null || measure.empty || measure.tooLong}
            onClick={() => this.saveDraft()}
            data-testid="prompt-save-draft"
          >
            {busy === 'draft' ? 'Enregistrement…' : 'Enregistrer le brouillon'}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={busy !== null || !publishable.ok}
            onClick={this.openPublish}
            data-testid="prompt-publish"
          >
            Publier…
          </Button>
          <Button size="small" disabled={busy !== null} onClick={this.cancelEditing} data-testid="prompt-cancel">
            Annuler
          </Button>
        </Stack>
        {!publishable.ok && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} data-testid="prompt-publish-reason">
            Publication indisponible : {publishable.reason}
          </Typography>
        )}
      </Box>
    );
  }

  renderHistory() {
    const { entry } = this.props;
    const { versions, versionsLoading, versionsError, busy } = this.state;
    if (versionsLoading) return <CircularProgress size={18} sx={{ mt: 1 }} />;
    if (versionsError) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          Historique illisible : {versionsError}
        </Alert>
      );
    }
    if (!Array.isArray(versions)) return null;
    if (!versions.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Aucune version au registre.
        </Typography>
      );
    }
    const currentInternal = entry.published ? entry.published.internal_version : null;
    const canRollbackAny = entry.reconciliation === RECONCILIATION.IN_SYNC && entry.publishableHere && !entry.missing;
    const onlyOne = entry.versionsCount <= 1 || versions.filter((v) => v.status !== 'draft').length <= 1;
    return (
      <Stack spacing={1} sx={{ mt: 1 }} data-testid="prompt-history">
        {onlyOne && (
          <Typography variant="caption" color="text.secondary">
            Une seule version enregistrée : il n'y a pas d'antérieur vers lequel revenir (le retour arrière
            s'active à partir de la deuxième publication).
          </Typography>
        )}
        {versions.map((version) => {
          const isCurrent = version.status === 'published' || version.internal_version === currentInternal;
          const isDraft = version.status === 'draft';
          const rollbackReason = isDraft
            ? 'brouillon, jamais publié'
            : isCurrent
            ? 'version en service'
            : typeof version.mistral_version !== 'number'
            ? 'numéro Mistral inconnu'
            : !canRollbackAny
            ? 'retour arrière bloqué (état non vérifiable, dérive ou environnement)'
            : null;
          return (
            <Box key={version.internal_version} sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }} data-testid="prompt-version">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Typography variant="body2">
                  Version interne {formatNumber(version.internal_version)}
                  {typeof version.mistral_version === 'number' ? ` · n° ${formatNumber(version.mistral_version)} chez Mistral` : ' · jamais publiée'}
                  {' · '}
                  {isDraft ? 'brouillon' : isCurrent ? 'en service' : 'remplacée'}
                  {version.origin === 'adopted' ? ' · adoptée' : ''}
                  {version.verified === false ? ' · application non confirmée' : ''}
                  {' — par '}
                  {authorName(version.author)}
                  {version.published_at
                    ? `, publiée le ${formatDate(version.published_at)}`
                    : version.updated_at
                    ? `, modifiée le ${formatDate(version.updated_at)}`
                    : ''}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy !== null || rollbackReason !== null}
                  onClick={() => this.openRollback(version)}
                  data-testid="prompt-rollback"
                  data-version={version.internal_version}
                >
                  Revenir à cette version
                </Button>
              </Stack>
              {rollbackReason && (
                <Typography variant="caption" color="text.secondary">
                  Retour arrière indisponible : {rollbackReason}.
                </Typography>
              )}
              {version.note && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  « {version.note} »
                </Typography>
              )}
              {typeof version.instructions === 'string' && (
                <Accordion disableGutters elevation={0} square sx={{ bgcolor: 'transparent' }} TransitionProps={{ unmountOnExit: true }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32 }}>
                    <Typography variant="caption" color="text.secondary">
                      Texte ({formatNumber(codePointLength(version.instructions))} caractères)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <Box
                      component="pre"
                      sx={{ m: 0, p: 2, maxHeight: 360, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {version.instructions}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          );
        })}
      </Stack>
    );
  }

  renderConfirmDialog() {
    const { confirm, busy } = this.state;
    const open = confirm !== null;
    if (!open) return null;

    if (confirm.kind === 'conflict') {
      const current = confirm.current || {};
      const who = authorName(current.author);
      const when = current.updated_at ? ` le ${formatDate(current.updated_at)}` : '';
      // L2 — la boîte dit ce qu'elle fait VRAIMENT selon le geste refusé : sur « publier », confirmer
      // remplace le brouillon d'un autre ET publie pour tout le parc (le refus 409 est arrivé AVANT
      // tout PATCH : rien n'est encore parti chez Mistral).
      const isPublish = confirm.gesture === 'publish';
      return (
        <Dialog open onClose={this.closeConfirm} data-testid="prompt-confirm-dialog" data-gesture={confirm.gesture}>
          <DialogTitle>
            {isPublish ? `Publier par-dessus le brouillon de ${who} ?` : `Remplacer le brouillon de ${who} ?`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              Un brouillon a été enregistré par {who}{when}. Rien n'a encore été envoyé chez Mistral. Les brouillons
              ne sont pas historisés : le remplacer est définitif.
            </DialogContentText>
            {isPublish && (
              <DialogContentText>
                En confirmant, ton texte remplace ce brouillon <strong>et</strong> est publié immédiatement.{' '}
                {PUBLISH_CONSEQUENCE}
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeConfirm} disabled={busy !== null}>Annuler</Button>
            <Button onClick={this.confirmAction} color="error" variant="contained" disabled={busy !== null} data-testid="prompt-confirm-ok">
              {isPublish ? 'Remplacer et publier' : 'Remplacer quand même'}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    const isRollback = confirm.kind === 'rollback';
    const note = confirm.note || '';
    const delta = confirm.delta;
    return (
      <Dialog open onClose={this.closeConfirm} fullWidth maxWidth="sm" data-testid="prompt-confirm-dialog">
        <DialogTitle>
          {isRollback
            ? `Revenir à la version interne ${formatNumber(confirm.version.internal_version)} ?`
            : 'Publier cette consigne ?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>{PUBLISH_CONSEQUENCE}</DialogContentText>
          {isRollback ? (
            <DialogContentText sx={{ mb: 1 }}>
              Le retour arrière <strong>crée une nouvelle version</strong> qui recopie la version{' '}
              {formatNumber(confirm.version.internal_version)} (n° {formatNumber(confirm.version.mistral_version)} chez
              Mistral) — le compteur ne revient pas en arrière.
            </DialogContentText>
          ) : (
            delta && (
              <DialogContentText sx={{ mb: 1 }} data-testid="prompt-confirm-delta">
                {delta.bounded
                  ? `${signedNumber(delta.net)} caractères au total (${formatNumber(delta.referenceLength)} → ${formatNumber(
                      delta.nextLength
                    )}) — texte trop long pour le détail ligne à ligne.`
                  : `${formatNumber(delta.removed)} caractères retirés, ${formatNumber(delta.added)} ajoutés dans les lignes modifiées (${formatNumber(
                      delta.lines.removed
                    )} lignes retirées, ${formatNumber(delta.lines.added)} ajoutées) — ${signedNumber(delta.net)} au total (${formatNumber(
                      delta.referenceLength
                    )} → ${formatNumber(delta.nextLength)})`}
                {confirm.referenceSource === SOURCE_REGISTRY ? ', par rapport à la version du registre (texte en service non lu par cet écran).' : ', par rapport au texte en service.'}
              </DialogContentText>
            )
          )}
          <TextField
            autoFocus
            fullWidth
            required
            label="Justification (obligatoire)"
            placeholder="Pourquoi ce changement ?"
            value={note}
            onChange={this.handleNoteChange}
            disabled={busy !== null}
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            inputProps={{ 'data-testid': 'prompt-confirm-note' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeConfirm} disabled={busy !== null}>Annuler</Button>
          <Button
            onClick={this.confirmAction}
            color="warning"
            variant="contained"
            disabled={busy !== null || note.trim().length === 0}
            data-testid="prompt-confirm-ok"
          >
            {busy === 'publish' || busy === 'rollback' ? 'En cours…' : isRollback ? 'Confirmer le retour arrière' : 'Confirmer la publication'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { entry } = this.props;
    const { editing, busy, historyOpen } = this.state;
    const editability = this.editability();

    return (
      <Box sx={{ mt: 2 }}>
        {this.renderReconciliation()}
        {this.renderRegistryLine()}
        {this.renderNotice()}
        {editing ? (
          this.renderEditor()
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Button
              size="small"
              variant="contained"
              disabled={busy !== null || !editability.ok}
              onClick={this.startEditing}
              data-testid="prompt-edit-button"
            >
              {busy === 'versions' ? 'Chargement…' : 'Modifier'}
            </Button>
            <Button size="small" variant="outlined" onClick={this.toggleHistory} data-testid="prompt-history-toggle">
              {historyOpen ? 'Masquer l\'historique' : `Historique (${formatNumber(entry.versionsCount)})`}
            </Button>
            {!editability.ok && (
              <Typography variant="caption" color="text.secondary" data-testid="prompt-edit-reason">
                {editability.reason}
              </Typography>
            )}
          </Stack>
        )}
        <Collapse in={historyOpen} unmountOnExit>
          {this.renderHistory()}
        </Collapse>
        {this.renderConfirmDialog()}
      </Box>
    );
  }
}

export default PromptEditor;
